import { Dispatch, SetStateAction } from 'react';
import { Col, Row } from 'antd5';
import { Button } from 'components/ui';
import {
  AtlasGqlInspectionType,
  AtlasGqlMetadataField,
  AtlasGqlMetadataFieldMapping,
} from 'types/atlas-graphql';
import { Steps } from '../UploadInspectionDataModal';
import { ButtonContainer, ErrorText, LabelColumn } from '../UploadInspectionDataModal.style';
import { useUploadContext } from 'components/FileUploader';

type PreviewProps = {
  inspectionCount: number;
  inspectionType?: AtlasGqlInspectionType;
  internalFields: AtlasGqlMetadataField[];
  fieldMappings: AtlasGqlMetadataFieldMapping[];
  records: { [key: string]: string }[];
  setStep: Dispatch<SetStateAction<Steps>>;
  handleComplete: () => void;
};

export const Preview: React.FC<PreviewProps> = ({
  inspectionCount,
  inspectionType,
  internalFields,
  fieldMappings,
  records,
  setStep,
  handleComplete,
}) => {
  const { uploading } = useUploadContext();

  const inspectionTypeFieldName = fieldMappings.find(fieldMapping => {
    const internalTypeField = internalFields.find(field => field.displayName === 'Inspection Type');
    return fieldMapping.targetFieldName === internalTypeField?.internalName;
  });
  const inspectionTypes = inspectionTypeFieldName
    ? records.map(record => record[inspectionTypeFieldName.sourceFieldName])
    : [];

  const isValid = inspectionType?.name
    ? inspectionTypes.every(type => inspectionType?.name === type)
    : inspectionTypes.every(type => inspectionTypes[0] === type);

  const previewRecord = records[0];
  return (
    <>
      {!isValid ? (
        <>
          <ErrorText>
            <strong>
              All Inspections must be of the same type
              {inspectionType?.name ? `: ${inspectionType?.name}` : '.'}
            </strong>
            <br />
            <p>Please use the button below to upload again.</p>
          </ErrorText>
        </>
      ) : (
        <>
          <p>
            This will create {inspectionCount} inspection{inspectionCount === 1 ? '' : 's'}.
          </p>
          <p>Preview of the first uploaded inspection:</p>
          {fieldMappings.map(({ sourceFieldName, targetFieldName }) => (
            <Row key={targetFieldName}>
              <Col flex="10%" />
              <LabelColumn flex="auto">
                <label>
                  {internalFields.find(field => field.internalName === targetFieldName)
                    ?.displayName ?? targetFieldName}
                  :
                </label>
              </LabelColumn>
              <Col flex="60%">{previewRecord[sourceFieldName]}</Col>
            </Row>
          ))}
        </>
      )}

      <ButtonContainer>
        {isValid ? (
          <>
            <Button onClick={() => setStep(Steps.SummaryMapping)}>Back</Button>
            <Button type="primary" onClick={handleComplete} loading={uploading}>
              Continue
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep(Steps.SummaryUpload)}>Reupload</Button>
          </>
        )}
      </ButtonContainer>
    </>
  );
};
